import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useMediaQuery } from 'react-responsive';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ImageIcon from '@mui/icons-material/Image';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';
import { ItemDrawer } from './FacilitiesReview';

function fmtDate(v) {
  if (!v) return '—';
  try { return format(typeof v === 'string' ? parseISO(v) : v, 'MMM d, yyyy'); }
  catch { return v; }
}

function statusChipColor(status) {
  if (status === 'Completed') return 'success';
  if (status === 'In Progress') return 'info';
  if (status === 'Deferred') return 'default';
  return 'warning';
}

export default function FacilitiesReviewWalkthrough() {
  const { fwtId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 900px)' });
  const { user: firebaseUser } = useUserAuth();

  // If the user arrived from /facilities-scores, back should send them there.
  // Direct arrivals (e.g. from an email link) default to the open-items page.
  const back = location.state?.from === 'scores'
    ? { label: 'Back to scores', path: '/facilities-scores' }
    : { label: 'Back to all open items', path: '/facilities-review' };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawerFrId, setDrawerFrId] = useState(null);

  const load = useCallback(async () => {
    if (!firebaseUser?.email) return;
    setLoading(true);
    setError(null);
    try {
      const token = await firebaseUser.getIdToken(true);
      const { data: resp } = await axios.get(
        `/hd-api/facilities-review/walkthrough/${encodeURIComponent(fwtId)}?user=${encodeURIComponent(firebaseUser.email)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(resp);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load walkthrough');
    } finally {
      setLoading(false);
    }
  }, [fwtId, firebaseUser]);

  useEffect(() => { load(); }, [load]);

  const walkthrough = data?.walkthrough;
  const items = data?.items || [];

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(back.path)}
            sx={{ mb: 1 }}
          >
            {back.label}
          </Button>

          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Box sx={{
              backgroundColor: 'white',
              borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
              px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2,
            }}>
              <Box sx={{
                backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                borderRadius: 2, p: 1, display: 'flex', alignItems: 'center',
              }}>
                <AssignmentIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato' }}>
                  {walkthrough?.storeName || 'Walkthrough'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {walkthrough?.dirOps ? `DO: ${walkthrough.dirOps} · ` : ''}
                  Submitted {fmtDate(walkthrough?.businessDate)}
                  {walkthrough?.userEmail ? ` by ${walkthrough.userEmail}` : ''}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading && !data && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          )}

          {walkthrough && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Pass %" value={walkthrough.percentage != null ? `${walkthrough.percentage}%` : '—'} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Scored / Potential" value={`${walkthrough.scored} / ${walkthrough.scored + walkthrough.missed}`} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard label="Open items" value={walkthrough.openItemsCount} />
                </Grid>
              </Grid>

              <Paper variant="outlined">
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2">Failed items ({items.length})</Typography>
                </Box>
                {items.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No failed items with descriptions on this walkthrough.</Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Area</TableCell>
                          <TableCell>Item</TableCell>
                          <TableCell>Issue</TableCell>
                          <TableCell>Owner</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Photos</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((r) => (
                          <TableRow key={r.frID} hover>
                            <TableCell>{r.area || '—'}</TableCell>
                            <TableCell>{r.item || '—'}</TableCell>
                            <TableCell>{r.issue}</TableCell>
                            <TableCell>{r.owner || '—'}</TableCell>
                            <TableCell>
                              <Chip size="small" label={r.status} color={statusChipColor(r.status)} />
                            </TableCell>
                            <TableCell align="right">
                              {r.imageCount > 0 ? (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                  <ImageIcon fontSize="small" color="action" />
                                  {r.imageCount}
                                </Box>
                              ) : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined" onClick={() => setDrawerFrId(r.frID)}>
                                Update
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </>
          )}
        </Box>
      </Container>

      <ItemDrawer
        open={drawerFrId != null}
        frId={drawerFrId}
        onClose={() => setDrawerFrId(null)}
        userEmail={firebaseUser?.email}
        onSaved={() => { setDrawerFrId(null); load(); }}
      />
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, mt: 0.5 }}>{value ?? '—'}</Typography>
    </Paper>
  );
}
